import { useCallback, useEffect, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { SIZE } from 'baseui/input'
import useHttp from '@/shared/hooks/useHttp'
import { useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import SDSelect from '@/shared/components/SDComponents/SDSelect/SDSelect'
import { HOPButtonGroup } from '@/shared/components/HOPComponents/HOPButtonGroup'
import { TYPE } from 'baseui/select'
import { FormControl } from 'baseui/form-control'
import { Textarea } from 'baseui/textarea'
const mapProviderList = list => {
  if (!list) return []
  return Object.values(list).map(provider => ({
    label: provider.name,
    uuid: provider.uuid,
  }))
}

const batchPdlProviderData = {
  initialized: false,
  general: { providersList: [] },
}

const batchPdlProviderActions = (state, action) => {
  if (action.type === 'initialize') {
    return {
      ...state,
      initialized: true,
      general: {
        providersList: mapProviderList(action.payload.organisations ?? []),
      },
    }
  }
  return state
}
import useBusinessType from '@/shared/hooks/useBusinessType'
import { useTranslation } from 'react-i18next'
import { useStyletron } from 'baseui'
import { Button } from 'baseui/button'
import { Notification } from 'baseui/notification'
import { Card, StyledBody } from 'baseui/card'

const BatchPdlUpdaterTool = ({ batchPdlUpdaterUrl }) => {
  const { t } = useTranslation()
  const { loaderRoutesEnergy, loaderRoutes, loaderRoutesInsurance, country } = useSelector(selectAuthData)
  const { getApplicationUrl } = useBusinessType({
    loaderRoutesEnergy,
    loaderRoutes,
    loaderRoutesInsurance,
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })
  const initUrl = getApplicationUrl('organizations-management')
  const { sendRequest, getConfig } = useHttp()
  const [providerId, setProviderId] = useState('')
  const [updateAction, setUpdateAction] = useState('net')
  const [pdlList, setPdlList] = useState([])
  const [providerState, providerDispatch] = useReducer(batchPdlProviderActions, batchPdlProviderData)
  const [error, setError] = useState(false)
  const [responseMessage, setResponseMessage] = useState(null)
  const [css, theme] = useStyletron()
  const handleRequest = useCallback(async () => {
    const headers = getConfig('POST')
    const options = new URLSearchParams()
    options.append('provider', providerId[0].uuid ?? '')
    options.append('action', updateAction)
    const pdlArray = pdlList
      .split('\n')
      .map(pdl => pdl.trim())
      .filter(pdl => pdl.length > 0)
    options.append('pdlList', JSON.stringify(pdlArray))
    headers.body = options

    sendRequest(`${batchPdlUpdaterUrl}?lang=${country}`, headers, (response, statusCode) => {
      if (statusCode === 200) {
        setError(false)
        setResponseMessage(response.message || '')
      } else {
        setError(true)
        setResponseMessage(response.error || t('devTools:batch_pdl_updater.unknown_error'))
      }
    })
  }, [getConfig, country, pdlList, providerId, sendRequest, t])

  const initialize = useCallback(async () => {
    if (!initUrl) return

    await sendRequest(`${initUrl}?lang=${country}`, getConfig('GET'), response =>
      providerDispatch({ type: 'initialize', payload: response }),
    )
  }, [getConfig, initUrl, country, sendRequest])

  useEffect(() => {
    initialize()
  }, [])

  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        width: '100%',
      })}
    >
      <Card
        overrides={{
          Root: {
            style: {
              width: '100%',
              maxWidth: '800px',
            },
          },
        }}
      >
        <StyledBody>
          <HOPButtonGroup
            onClickHandler={value => setUpdateAction(value)}
            options={[
              { net: t('devTools:batch_pdl_updater.net') },
              { abandon: t('devTools:batch_pdl_updater.abandon') },
            ]}
            selectedValue={updateAction}
          />

          <FormControl label={t('devTools:batch_pdl_updater.select_provider_label')}>
            <SDSelect
              id='provider'
              type={TYPE.search}
              options={providerState.general.providersList}
              value={providerId}
              onChange={params => setProviderId(params.value)}
            />
          </FormControl>

          <FormControl label={t('devTools:batch_pdl_updater.pdl_list_label')}>
            <Textarea value={pdlList} onChange={e => setPdlList(e.target.value)} placeholder='PDL/PCE list' clearOnEscape />
          </FormControl>

          <Button
            size={SIZE.compact}
            onClick={event => {
              event.preventDefault()
              handleRequest()
            }}
            disabled={!providerId.length}
            overrides={{
              BaseButton: {
                style: {
                  width: '100%',
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.white,
                },
              },
            }}
          >
            {updateAction === 'net'
              ? t('devTools:batch_pdl_updater.update_net_button')
              : t('devTools:batch_pdl_updater.update_abandon_button')}
          </Button>

          {responseMessage && (
            <Notification
              kind={error ? 'negative' : 'positive'}
              overrides={{
                Body: {
                  style: {
                    marginTop: '20px',
                    width: '100%',
                    maxWidth: '800px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  },
                },
              }}
            >
              {responseMessage}
            </Notification>
          )}
        </StyledBody>
      </Card>
    </div>
  )
}

BatchPdlUpdaterTool.propTypes = {
  batchPdlUpdaterUrl: PropTypes.string.isRequired,
}

export default BatchPdlUpdaterTool
